import { useEffect, useRef, useState } from "react";
import { Camera, FloppyDisk, Trash, User, EnvelopeSimple, Phone, Cake, Ruler, Target } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function ClientProfile() {
  const { refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/clients/me/profile");
      setProfile(data.profile);
      setForm({
        name: data.profile.name || "",
        phone: data.profile.phone || "",
        whatsapp_number: data.profile.whatsapp_number || "",
        date_of_birth: data.profile.date_of_birth || "",
        height_cm: data.profile.height_cm || "",
        goals: data.profile.goals || "",
        profile_image_b64: data.profile.profile_image_b64 || "",
      });
      setDirty(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load profile");
    }
  };
  useEffect(() => { load(); }, []);

  const onChange = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Image too large — please use one under 1.5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange("profile_image_b64", reader.result);
    reader.readAsDataURL(file);
  };

  const removePhoto = () => onChange("profile_image_b64", "");

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        phone: form.phone || null,
        whatsapp_number: form.whatsapp_number || null,
        date_of_birth: form.date_of_birth || null,
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        goals: form.goals || null,
        profile_image_b64: form.profile_image_b64 || null,
      };
      await api.patch("/clients/me/profile", payload);
      toast.success("Profile saved");
      setDirty(false);
      if (refresh) await refresh();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!profile || !form) return <div className="tracker text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="client-profile-page">
      <div>
        <div className="tracker text-brand mb-3"><User size={14} weight="duotone" className="inline mr-1.5" /> [ Profile ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Your profile.</h1>
        <p className="text-zinc-500 text-sm mt-2">Keep your details up to date so your coach can program for you.</p>
      </div>

      {/* Photo */}
      <section>
        <div className="tracker text-zinc-500 mb-3">[ PHOTO ]</div>
        <div className="flex items-center gap-5">
          {form.profile_image_b64 ? (
            <img src={form.profile_image_b64} alt="" className="w-24 h-24 object-cover border border-white/10" data-testid="profile-photo-preview" />
          ) : (
            <div className="w-24 h-24 bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display text-3xl text-zinc-600" data-testid="profile-photo-empty">
              {(profile.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="profile-photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="profile-photo-upload">
              <Camera size={12} weight="bold" /> {form.profile_image_b64 ? "Change photo" : "Upload photo"}
            </button>
            {form.profile_image_b64 && (
              <button type="button" onClick={removePhoto} className="btn-ghost !py-2 !px-3 !text-xs text-red-400" data-testid="profile-photo-remove">
                <Trash size={12} weight="bold" /> Remove
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Identity */}
      <section className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1">FULL NAME</div>
          <input value={form.name} onChange={(e) => onChange("name", e.target.value)} className="w-full px-3 py-2.5 border bg-[#0A0A0A] text-sm" data-testid="profile-name-input" />
        </label>
        <div className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1">EMAIL (read-only)</div>
          <div className="w-full px-3 py-2.5 border border-white/10 bg-[#0A0A0A] text-sm font-mono text-zinc-400 inline-flex items-center gap-2">
            <EnvelopeSimple size={12} weight="bold" /> {profile.email}
          </div>
        </div>
        <label className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1">PHONE</div>
          <input value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="+44 ..." className="w-full px-3 py-2.5 border bg-[#0A0A0A] text-sm font-mono" data-testid="profile-phone-input" />
        </label>
        <label className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1">WHATSAPP NUMBER (E.164)</div>
          <input value={form.whatsapp_number} onChange={(e) => onChange("whatsapp_number", e.target.value)} placeholder="+447700900100" className="w-full px-3 py-2.5 border bg-[#0A0A0A] text-sm font-mono" data-testid="profile-whatsapp-input" />
        </label>
      </section>

      {/* Body basics */}
      <section className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1"><Cake size={11} weight="bold" className="inline mr-1" /> DATE OF BIRTH</div>
          <input type="date" value={form.date_of_birth} onChange={(e) => onChange("date_of_birth", e.target.value)} className="w-full px-3 py-2.5 border bg-[#0A0A0A] text-sm" data-testid="profile-dob-input" />
        </label>
        <label className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1"><Ruler size={11} weight="bold" className="inline mr-1" /> HEIGHT (cm)</div>
          <input type="number" inputMode="numeric" min={50} max={300} step="0.5" value={form.height_cm} onChange={(e) => onChange("height_cm", e.target.value)} className="w-full px-3 py-2.5 border bg-[#0A0A0A] text-sm font-mono" data-testid="profile-height-input" />
        </label>
      </section>

      {/* Goals */}
      <section>
        <label className="block">
          <div className="tracker text-zinc-500 text-[10px] mb-1"><Target size={11} weight="bold" className="inline mr-1" /> GOALS</div>
          <textarea
            value={form.goals}
            onChange={(e) => onChange("goals", e.target.value)}
            placeholder="What are you working towards? Your coach sees this."
            rows={5}
            maxLength={2000}
            className="w-full px-3 py-2.5 border bg-[#0A0A0A] text-sm"
            data-testid="profile-goals-input"
          />
          <div className="tracker text-[10px] text-zinc-600 mt-1 text-right">{(form.goals || "").length}/2000</div>
        </label>
      </section>

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="profile-save"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : dirty ? "Save profile" : "Saved"}
        </button>
      </div>
    </div>
  );
}
